import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Link2, Unlink, RefreshCw, CheckCircle, Users, DollarSign, Clock, ExternalLink, Shield, Settings, X, AlertCircle, Zap, Save } from 'lucide-react';

// Provider-specific config field definitions
const PROVIDER_CONFIGS = {
  workday: { fields: [
    { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter Workday API key' },
    { key: 'tenant_url', label: 'Tenant URL', type: 'text', placeholder: 'https://wd5-impl-services1.workday.com/...' },
    { key: 'username', label: 'Integration User', type: 'text', placeholder: 'ISU_username' },
    { key: 'password', label: 'Integration Password', type: 'password', placeholder: 'Password' },
  ], docs: 'https://community.workday.com/sites/default/files/file-hosting/restapi/' },
  gusto: { fields: [
    { key: 'api_key', label: 'API Token', type: 'password', placeholder: 'Enter Gusto API token' },
    { key: 'company_id', label: 'Company UUID', type: 'text', placeholder: 'Your Gusto company UUID' },
    { key: 'environment', label: 'Environment', type: 'select', options: ['sandbox', 'production'] },
  ], docs: 'https://docs.gusto.com/embedded-payroll/docs' },
  bamboohr: { fields: [
    { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Enter BambooHR API key' },
    { key: 'subdomain', label: 'Company Subdomain', type: 'text', placeholder: 'yourcompany (from yourcompany.bamboohr.com)' },
  ], docs: 'https://documentation.bamboohr.com/reference' },
  sso_oidc: { fields: [
    { key: 'issuer_url', label: 'Issuer URL', type: 'text', placeholder: 'https://login.microsoftonline.com/{tenant}/v2.0' },
    { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'Application (client) ID' },
    { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'Client secret value' },
    { key: 'redirect_uri', label: 'Redirect URI', type: 'text', placeholder: 'https://yourdomain.com/auth/callback' },
    { key: 'scopes', label: 'Scopes', type: 'text', placeholder: 'openid profile email' },
    { key: 'domains', label: 'Allowed Email Domains', type: 'text', placeholder: 'yourcompany.com, subsidiary.com' },
  ], docs: 'https://openid.net/developers/how-connect-works/' },
  adp: { fields: [
    { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'ADP Marketplace Client ID' },
    { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'ADP Client Secret' },
    { key: 'company_code', label: 'Company Code', type: 'text', placeholder: 'Your ADP company code' },
    { key: 'environment', label: 'Environment', type: 'select', options: ['sandbox', 'production'] },
    { key: 'cert_path', label: 'SSL Certificate Path', type: 'text', placeholder: '/path/to/cert.pem (optional)' },
  ], docs: 'https://developers.adp.com/articles/guides' },
  google_workspace: { fields: [
    { key: 'client_id', label: 'OAuth Client ID', type: 'text', placeholder: 'xxxx.apps.googleusercontent.com' },
    { key: 'client_secret', label: 'OAuth Client Secret', type: 'password', placeholder: 'GOCSPX-...' },
    { key: 'service_account', label: 'Service Account Email', type: 'text', placeholder: 'sa@project.iam.gserviceaccount.com' },
    { key: 'admin_email', label: 'Google Admin Email', type: 'text', placeholder: 'admin@yourcompany.com' },
    { key: 'scopes', label: 'API Scopes', type: 'text', placeholder: 'gmail.send, calendar.events, drive.file' },
  ], docs: 'https://developers.google.com/workspace/guides/create-credentials' },
  zoom: { fields: [
    { key: 'account_id', label: 'Account ID', type: 'text', placeholder: 'Your Zoom Account ID' },
    { key: 'client_id', label: 'Client ID', type: 'text', placeholder: 'Server-to-Server OAuth Client ID' },
    { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'Client Secret' },
    { key: 'webhook_token', label: 'Webhook Verification Token', type: 'text', placeholder: 'Optional — for event notifications' },
  ], docs: 'https://developers.zoom.us/docs/internal-apps/s2s-oauth/' },
};

export default function Integrations() {
  const [providers, setProviders] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [configModal, setConfigModal] = useState(null); // { provider, connection }
  const [configForm, setConfigForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [viewingData, setViewingData] = useState(null);
  const [syncedData, setSyncedData] = useState([]);

  useEffect(() => { loadData(); }, []);
  const loadData = async () => {
    try {
      const [provRes, connRes] = await Promise.all([api.get('/api/integrations/all-providers'), api.get('/api/integrations/connections')]);
      setProviders(provRes.data || []); setConnections(connRes.data || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const getConnection = (providerId) => connections.find(c => c.provider_id === providerId);

  const openConfig = (provider, conn) => {
    setConfigModal({ provider, connection: conn });
    setConfigForm(conn?.config || {});
    setTestResult(null);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      if (configModal.connection) {
        await api.patch(`/api/integrations/${configModal.connection.id}/config`, { config: configForm });
      } else {
        await api.post('/api/integrations/connect', { provider_id: configModal.provider.id, config: configForm });
      }
      setConfigModal(null); loadData();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleTest = async (connId) => {
    setTesting(connId); setTestResult(null);
    try {
      const r = await api.post(`/api/integrations/${connId}/test`);
      setTestResult(r.data);
    } catch (e) { setTestResult({ success: false, errors: [e.response?.data?.detail || 'Test failed'] }); }
    finally { setTesting(null); }
  };

  const handleSync = async (connId) => {
    setSyncing(connId);
    try { await api.post(`/api/integrations/sync/${connId}`); loadData(); } catch(e){ console.error(e); }
    finally { setSyncing(null); }
  };

  const handleDisconnect = async (connId) => {
    if (!window.confirm('Disconnect this integration? Configuration will be removed.')) return;
    try { await api.delete(`/api/integrations/disconnect/${connId}`); setViewingData(null); loadData(); } catch(e){ console.error(e); }
  };

  const handleViewData = async (connId) => {
    setViewingData(connId);
    try { const r = await api.get(`/api/integrations/synced-data/${connId}`); setSyncedData(r.data || []); } catch(e){ console.error(e); }
  };

  if (loading) return <div className="loading-screen" style={{ height: 400, background: 'transparent' }}><div className="spinner" style={{ borderTopColor: 'var(--panzix-green)' }} /></div>;

  return (
    <div className="fade-in" style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Integrations</h3>
        <p style={{ fontSize: 14, color: '#64748b' }}>Connect your HR tools, payroll systems, and productivity apps. Configure API credentials to enable data sync.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
        {providers.map(provider => {
          const conn = getConnection(provider.id);
          const isVerified = conn?.status === 'verified';
          return (
            <div key={provider.id} style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', transition: 'all 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'} onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.06)'}
              data-testid={`provider-${provider.id}`}>
              <div style={{ height: 4, background: provider.color }} />
              <div style={{ padding: 22 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                  <div style={{ width: 46, height: 46, borderRadius: 12, background: provider.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: provider.logo.length > 2 ? 12 : 20, fontWeight: 800 }}>{provider.logo}</div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontSize: 17, fontWeight: 700 }}>{provider.name}</h4>
                    <p style={{ fontSize: 12, color: '#64748b' }}>{provider.description}</p>
                  </div>
                  {conn && (
                    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: isVerified ? '#dcfce7' : '#fef3c7', color: isVerified ? '#166534' : '#92400e', display: 'flex', alignItems: 'center', gap: 4 }}>
                      {isVerified ? <><CheckCircle size={10} />Verified</> : <><Clock size={10} />Connected</>}
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 16 }}>
                  {provider.features.map(f => <span key={f} className="specialty-tag" style={{ fontSize: 10 }}>{f}</span>)}
                </div>

                {conn ? (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button onClick={() => openConfig(provider, conn)} className="btn-action" style={{ flex: 1, padding: '9px 14px', fontSize: 12, borderRadius: 10 }}>
                      <Settings size={13} style={{ marginRight: 4 }} />Configure
                    </button>
                    {provider.sample_data_type !== 'none' && (
                      <button onClick={() => handleSync(conn.id)} disabled={syncing === conn.id} className="btn-secondary" style={{ padding: '9px 12px', fontSize: 12, borderRadius: 10 }}>
                        <RefreshCw size={13} className={syncing === conn.id ? 'spin-icon' : ''} />
                      </button>
                    )}
                    {conn.synced_records > 0 && (
                      <button onClick={() => handleViewData(conn.id)} className="btn-secondary" style={{ padding: '9px 12px', fontSize: 12, borderRadius: 10 }}><ExternalLink size={13} /></button>
                    )}
                    <button onClick={() => handleDisconnect(conn.id)} className="btn-delete" style={{ padding: '9px 12px', fontSize: 12, borderRadius: 10 }}><Unlink size={13} /></button>
                  </div>
                ) : (
                  <button onClick={() => openConfig(provider, null)} className="btn-action purple" style={{ width: '100%', padding: '11px', fontSize: 14, borderRadius: 10 }}>
                    <Link2 size={15} style={{ marginRight: 8 }} />Connect {provider.name}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Synced Data View */}
      {viewingData && (
        <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', padding: 24, marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
            <h4 style={{ fontSize: 16, fontWeight: 700 }}>Synced Data</h4>
            <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setViewingData(null)}>Close</button>
          </div>
          {syncedData.length === 0 ? <p style={{ color: '#94a3b8' }}>No data. Click Sync to pull data.</p> : (
            <table className="audit-log-table">
              <thead><tr>{syncedData[0] && Object.keys(syncedData[0]).filter(k => !['_id','id','connection_id','company_id','provider_id','synced_at','data_type'].includes(k)).slice(0,6).map(k => <th key={k}>{k.replace(/_/g,' ')}</th>)}</tr></thead>
              <tbody>{syncedData.slice(0,20).map((d,i) => <tr key={JSON.stringify(d).slice(0,30) + i}>{Object.entries(d).filter(([k]) => !['_id','id','connection_id','company_id','provider_id','synced_at','data_type'].includes(k)).slice(0,6).map(([k,v],j) => <td key={j}>{typeof v === 'number' ? (v > 999 ? `$${v.toLocaleString()}` : v) : String(v).slice(0,40)}</td>)}</tr>)}</tbody>
            </table>
          )}
        </div>
      )}

      {/* Configuration Modal */}
      {configModal && (
        <div className="task-modal-overlay" onClick={e => e.target === e.currentTarget && setConfigModal(null)}>
          <div className="task-modal slide-up" style={{ maxWidth: 580, maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: configModal.provider.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: configModal.provider.logo.length > 2 ? 10 : 18, fontWeight: 800 }}>{configModal.provider.logo}</div>
                <div>
                  <h3 style={{ margin: 0 }}>{configModal.connection ? 'Configure' : 'Connect'} {configModal.provider.name}</h3>
                  <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{configModal.provider.description}</p>
                </div>
              </div>
              <button onClick={() => setConfigModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>

            {/* Documentation link */}
            {PROVIDER_CONFIGS[configModal.provider.id]?.docs && (
              <a href={PROVIDER_CONFIGS[configModal.provider.id].docs} target="_blank" rel="noopener noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', background: '#f0fdf4', borderRadius: 10, fontSize: 12, color: '#166534', fontWeight: 600, textDecoration: 'none', marginBottom: 16, border: '1px solid #bbf7d0' }}>
                <ExternalLink size={13} />View {configModal.provider.name} API Documentation
              </a>
            )}

            {/* Config Fields */}
            {(PROVIDER_CONFIGS[configModal.provider.id]?.fields || []).map(field => (
              <div key={field.key} className="form-group">
                <label style={{ fontWeight: 600, fontSize: 13 }}>{field.label}</label>
                {field.type === 'select' ? (
                  <select value={configForm[field.key] || ''} onChange={e => setConfigForm(f => ({...f, [field.key]: e.target.value}))}
                    style={{ width: '100%', padding: '10px 14px', border: '2px solid #e2e8f0', borderRadius: 10, fontSize: 13 }} data-testid={`config-${field.key}`}>
                    <option value="">Select...</option>
                    {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={field.type} value={configForm[field.key] || ''} onChange={e => setConfigForm(f => ({...f, [field.key]: e.target.value}))}
                    placeholder={field.placeholder} style={{ fontSize: 13 }} data-testid={`config-${field.key}`} />
                )}
              </div>
            ))}

            {/* Test Result */}
            {testResult && (
              <div style={{ padding: 14, borderRadius: 10, marginBottom: 16, background: testResult.success ? '#dcfce7' : '#fef2f2', border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: testResult.errors ? 6 : 0 }}>
                  {testResult.success ? <CheckCircle size={16} color="#166534" /> : <AlertCircle size={16} color="#dc2626" />}
                  <span style={{ fontWeight: 600, fontSize: 13, color: testResult.success ? '#166534' : '#dc2626' }}>{testResult.success ? testResult.message : 'Configuration errors found'}</span>
                </div>
                {testResult.errors && testResult.errors.map((err, i) => <p key={i} style={{ fontSize: 12, color: '#dc2626', margin: '4px 0 0 22px' }}>&bull; {err}</p>)}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-action" onClick={handleSaveConfig} disabled={saving} style={{ flex: 1, borderRadius: 10, fontSize: 14, padding: '12px' }}>
                <Save size={15} style={{ marginRight: 6 }} />{saving ? 'Saving...' : configModal.connection ? 'Update Configuration' : 'Connect & Save'}
              </button>
              {configModal.connection && (
                <button onClick={() => handleTest(configModal.connection.id)} disabled={testing === configModal.connection.id} className="btn-secondary" style={{ borderRadius: 10, fontSize: 13, padding: '12px 18px' }}>
                  <Zap size={14} style={{ marginRight: 4 }} />{testing ? 'Testing...' : 'Test'}
                </button>
              )}
            </div>

            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 12, textAlign: 'center' }}>Credentials are encrypted and stored securely. Never shared with third parties.</p>
          </div>
        </div>
      )}
    </div>
  );
}
